import { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Lightbulb, Printer, RefreshCw, MessageCircle, Plus, Pencil, Archive, X } from 'lucide-react';
import { cn } from '../../../lib/utils.js';

const ABGLEICH_GROUPS = [
  { key: 'gruen', label: 'Passt so', icon: CheckCircle2, tone: 'border-emerald-200 bg-emerald-50 text-emerald-900', iconTone: 'text-emerald-600' },
  { key: 'gelb', label: 'Gemeinsam zu klären', icon: AlertCircle, tone: 'border-amber-200 bg-amber-50 text-amber-900', iconTone: 'text-amber-600' },
  { key: 'rot', label: 'Mein Vorschlag', icon: Lightbulb, tone: 'border-sky-200 bg-sky-50 text-sky-900', iconTone: 'text-sky-600' },
];

const EMPTY_FORM = { id: '', name: '', interesse: '', shop: '', stufe: 0, stand: '', naechsterSchritt: '' };

function Section({ title, testId, children, className, action }) {
  return (
    <section data-testid={testId} className={cn('rounded-xl border border-slate-200 bg-white p-5 shadow-xs space-y-4', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-extrabold text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function StageBar({ stufe, stufen }) {
  return (
    <ol className="flex gap-1" aria-label={`Fortschritt: ${stufen[stufe] || stufen[0]}`}>
      {stufen.map((label, i) => (
        <li key={label} title={label} className={cn('h-2 flex-1 rounded-full', i <= stufe ? 'bg-emerald-600' : 'bg-slate-200')} />
      ))}
    </ol>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE');
}

function Provenance({ k }) {
  if (!k.von || k.von === 'Seed') return null;
  return <span className="mt-1 block text-[11px] text-slate-500">Eintrag von {k.von}{k.updatedAt ? `, ${formatDate(k.updatedAt)}` : ''}</span>;
}

function CustomerForm({ stufen, initial, von, onVonChange, onSubmit, onCancel, busy, error }) {
  const [f, setF] = useState(initial);
  const set = (key) => (e) => setF((prev) => ({ ...prev, [key]: key === 'stufe' ? Number(e.target.value) : e.target.value }));
  const input = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[40px] focus:outline-none focus:ring-2 focus:ring-slate-300';
  return (
    <form
      data-testid="partner-customer-form"
      onSubmit={(e) => { e.preventDefault(); onSubmit(f); }}
      className="rounded-lg border border-slate-300 bg-slate-50 p-4 space-y-3 print:hidden"
    >
      <p className="text-sm font-bold text-slate-900">{f.id ? 'Eintrag ändern' : 'Neuen Kunden oder Interessenten eintragen'}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-xs font-bold text-slate-700">Kunde / Interessent *
          <input className={input} value={f.name} onChange={set('name')} maxLength={60} required />
        </label>
        <label className="text-xs font-bold text-slate-700">Ihr Name *
          <input className={input} value={von} onChange={(e) => onVonChange(e.target.value)} maxLength={40} required />
        </label>
        <label className="text-xs font-bold text-slate-700">Interesse (z. B. Schnittstelle, Shop)
          <input className={input} value={f.interesse} onChange={set('interesse')} maxLength={300} />
        </label>
        <label className="text-xs font-bold text-slate-700">Shop
          <input className={input} value={f.shop} onChange={set('shop')} maxLength={300} />
        </label>
        <label className="text-xs font-bold text-slate-700">Fortschritt
          <select className={input} value={f.stufe} onChange={set('stufe')}>
            {stufen.map((s, i) => <option key={s} value={i}>{s}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold text-slate-700">Stand / Neuigkeit
          <input className={input} value={f.stand} onChange={set('stand')} maxLength={300} />
        </label>
        <label className="text-xs font-bold text-slate-700 md:col-span-2">Nächster Schritt
          <input className={input} value={f.naechsterSchritt} onChange={set('naechsterSchritt')} maxLength={300} />
        </label>
      </div>
      {error && <p role="alert" className="text-xs font-bold text-red-700">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-bold text-white disabled:opacity-60 cursor-pointer">{busy ? 'Speichere …' : 'Speichern'}</button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 cursor-pointer"><X className="h-3 w-3" /> Abbrechen</button>
      </div>
    </form>
  );
}

/**
 * Partnerprogramm-Überblick für Herrn Manges. Daten kommen aus /api/portal/partner-dashboard
 * (Hub-Session erforderlich); Kunden werden über /api/portal/partner-customers gepflegt.
 * `initialData` dient Tests.
 */
export function PartnerProgramView({ initialData = null }) {
  const [data, setData] = useState(initialData);
  const [status, setStatus] = useState(initialData ? 'success' : 'loading');
  const [error, setError] = useState(null);

  const [form, setForm] = useState(null); // null = geschlossen, sonst Startwerte
  const [von, setVon] = useState(() => {
    try { return (typeof window !== 'undefined' && window.localStorage.getItem('partner-von')) || ''; } catch { return ''; }
  });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await fetch('/api/portal/partner-dashboard', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) throw new Error(body.error || `HTTP ${res.status}: Übersicht nicht verfügbar`);
      setData(body.dashboard);
      setStatus('success');
    } catch (err) {
      setError(err.message || 'Übersicht konnte nicht geladen werden');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!initialData) load();
  }, [initialData, load]);

  const changeVon = (v) => {
    setVon(v);
    try { window.localStorage.setItem('partner-von', v); } catch { /* ignorieren */ }
  };

  const post = async (payload) => {
    setBusy(true);
    setFormError(null);
    try {
      const res = await fetch('/api/portal/partner-customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) throw new Error(body.error || `HTTP ${res.status}: Speichern nicht möglich`);
      setData((d) => ({ ...d, kunden: body.customers }));
      setForm(null);
    } catch (err) {
      setFormError(err.message || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const save = (f) => post({ action: 'save', ...f, von });
  const archive = (k) => {
    if (!von.trim()) { setForm(EMPTY_FORM); setFormError('Bitte zuerst Ihren Namen eintragen.'); return; }
    if (typeof window !== 'undefined' && !window.confirm(`„${k.name}“ aus der Liste nehmen? (Wird archiviert, nicht gelöscht.)`)) return;
    post({ action: 'archive', id: k.id, von });
  };
  const edit = (k) => { setFormError(null); setForm({ id: k.id, name: k.name, interesse: k.interesse, shop: k.shop, stufe: k.stufe, stand: k.stand, naechsterSchritt: k.naechsterSchritt }); };

  if (status === 'loading') {
    return <div data-testid="partner-loading" className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">Übersicht wird geladen …</div>;
  }

  if (status === 'error' || !data) {
    return (
      <div data-testid="partner-error" role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-900 space-y-3">
        <p className="font-bold">Übersicht nicht verfügbar</p>
        <p>{error}</p>
        <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-lg bg-white border border-red-300 px-3 py-2 text-xs font-bold cursor-pointer">
          <RefreshCw className="h-4 w-4" /> Erneut versuchen
        </button>
      </div>
    );
  }

  const canEdit = data.kunden.every((k) => k.id);

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{data.title}</h1>
          <p className="text-xs text-slate-500 mt-1">Stand: {data.stand}</p>
        </div>
        <button type="button" onClick={() => window.print()} className="print:hidden inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer">
          <Printer className="h-4 w-4" /> Drucken / als PDF speichern
        </button>
      </div>

      <p data-testid="partner-hinweis" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{data.hinweis}</p>

      <Section title="Woran ich gerade arbeite" testId="partner-arbeit">
        <ul className="divide-y divide-slate-100">
          {data.aktuelleArbeit.map((a) => (
            <li key={a.id} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-sm text-slate-900">{a.titel}</span>
                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700">{a.status}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{a.text}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        title="Kunden und Interessenten"
        testId="partner-kunden"
        action={canEdit && !form && (
          <button type="button" data-testid="partner-add" onClick={() => { setFormError(null); setForm(EMPTY_FORM); }} className="print:hidden inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white cursor-pointer">
            <Plus className="h-4 w-4" /> Eintragen
          </button>
        )}
      >
        <p className="text-xs text-slate-500">Fortschritt: {data.stufen.join(' → ')}. Neue Kunden und Änderungen können Sie hier selbst eintragen.</p>
        {form && (
          <CustomerForm key={form.id || 'new'} stufen={data.stufen} initial={form} von={von} onVonChange={changeVon} onSubmit={save} onCancel={() => setForm(null)} busy={busy} error={formError} />
        )}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                <th className="py-2 pr-3">Kunde</th>
                <th className="py-2 pr-3">Interesse</th>
                <th className="py-2 pr-3">Shop</th>
                <th className="py-2 pr-3 w-32">Fortschritt</th>
                <th className="py-2 pr-3">Stand</th>
                <th className="py-2 pr-3">Nächster Schritt</th>
                {canEdit && <th className="py-2 print:hidden"><span className="sr-only">Aktionen</span></th>}
              </tr>
            </thead>
            <tbody className="align-top">
              {data.kunden.map((k) => (
                <tr key={k.id || k.name} className="border-b border-slate-100" data-testid={`partner-kunde-${k.name.toLowerCase()}`}>
                  <td className="py-3 pr-3 font-bold text-slate-900">{k.name}</td>
                  <td className="py-3 pr-3 text-slate-700">{k.interesse}</td>
                  <td className="py-3 pr-3 text-slate-700">{k.shop}</td>
                  <td className="py-3 pr-3"><StageBar stufe={k.stufe} stufen={data.stufen} /><span className="mt-1 block text-[11px] text-slate-500">{data.stufen[k.stufe]}</span></td>
                  <td className="py-3 pr-3 text-slate-700">{k.stand}<Provenance k={k} /></td>
                  <td className="py-3 pr-3 text-slate-700">{k.naechsterSchritt}</td>
                  {canEdit && (
                    <td className="py-3 print:hidden whitespace-nowrap">
                      <button type="button" aria-label={`${k.name} bearbeiten`} onClick={() => edit(k)} className="mr-1 rounded-md border border-slate-300 p-2 cursor-pointer"><Pencil className="h-4 w-4" /></button>
                      <button type="button" aria-label={`${k.name} archivieren`} onClick={() => archive(k)} className="rounded-md border border-slate-300 p-2 cursor-pointer"><Archive className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="md:hidden space-y-3" data-testid="partner-kunden-cards">
          {data.kunden.map((k) => (
            <li key={k.id || k.name} className="rounded-lg border border-slate-200 p-4 space-y-2 text-sm">
              <p className="font-bold text-slate-900">{k.name}</p>
              <StageBar stufe={k.stufe} stufen={data.stufen} />
              <p className="text-[11px] text-slate-500">{data.stufen[k.stufe]}</p>
              <p className="text-slate-700"><span className="font-bold">Interesse:</span> {k.interesse}</p>
              <p className="text-slate-700"><span className="font-bold">Shop:</span> {k.shop}</p>
              <p className="text-slate-700"><span className="font-bold">Stand:</span> {k.stand}<Provenance k={k} /></p>
              <p className="text-slate-700"><span className="font-bold">Nächster Schritt:</span> {k.naechsterSchritt}</p>
              {canEdit && (
                <div className="flex gap-2 pt-1 print:hidden">
                  <button type="button" onClick={() => edit(k)} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-bold cursor-pointer"><Pencil className="h-4 w-4" /> Bearbeiten</button>
                  <button type="button" onClick={() => archive(k)} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-bold cursor-pointer"><Archive className="h-4 w-4" /> Archivieren</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Offene Punkte zum Besprechen" testId="partner-rueckfragen">
        <ol className="space-y-3">
          {data.rueckfragen.map((q) => (
            <li key={q.nr} className="flex gap-3 text-sm text-slate-800">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-black text-white">{q.nr}</span>
              <span className="leading-relaxed">{q.frage}</span>
            </li>
          ))}
        </ol>
        <p className="flex items-center gap-2 text-xs text-slate-500"><MessageCircle className="h-4 w-4" /> Dazu gern in Ruhe telefonieren — ich melde mich separat per Mail.</p>
      </Section>

      <Section title="Eckpunkte zum Betriebsmodell" testId="partner-festlegungen">
        <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-700 leading-relaxed max-w-3xl">
          {data.festlegungen.map((f) => <li key={f}>{f}</li>)}
        </ol>
      </Section>

      <Section title={data.abgleich.titel} testId="partner-abgleich">
        <div className="grid gap-4 md:grid-cols-3">
          {ABGLEICH_GROUPS.map(({ key, label, icon: Icon, tone, iconTone }) => (
            <div key={key} className={cn('rounded-lg border p-4', tone)} data-testid={`partner-abgleich-${key}`}>
              <p className="flex items-center gap-2 text-sm font-extrabold"><Icon className={cn('h-4 w-4', iconTone)} /> {label} ({data.abgleich[key].length})</p>
              <ul className="mt-2 list-disc space-y-1.5 pl-5 text-xs leading-relaxed">
                {data.abgleich[key].map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
